import Box, { type BoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { capitalize } from '@mui/material/utils';

import { ColorPalette, type theme as BaseTheme } from '../theme';

import type { LabelClasses, LabelClassKey } from './label-classes';

enum LabelVariant {
  CONTAINED = 'contained',
  OUTLINED = 'outlined',
  TEXT = 'text',
}

enum LabelSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

enum AdditionLabelColor {
  GREY = 'grey',
}

type LabelColor = ColorPalette | AdditionLabelColor;
const LabelColor = {
  ...ColorPalette,
  ...AdditionLabelColor,
};

enum LabelBorderType {
  ROUND = 'round',
  RECTANGLE = 'rectangle',
}

interface LabelProps extends BoxProps {
  color?: `${LabelColor}`;
  variant?: `${LabelVariant}`;
  size?: `${LabelSize}`;
  borderType?: `${LabelBorderType}`;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
}

const LabelRoot = styled(Box, {
  name: 'Label',
  slot: 'Root',
  shouldForwardProp: (prop) =>
    prop !== 'color' &&
    prop !== 'variant' &&
    prop !== 'size' &&
    prop !== 'borderType' &&
    prop !== 'startIcon' &&
    prop !== 'endIcon',
  overridesResolver: (
    { theme, color: colorProps, variant: variantProps, size: sizeProps },
    styles,
  ) => {
    const defaultProps = theme.components?.['Label']?.defaultProps;

    const color = colorProps ?? defaultProps?.color ?? LabelColor.PRIMARY;
    const size = sizeProps ?? defaultProps?.size ?? LabelSize.MEDIUM;
    const variant = variantProps ?? defaultProps?.variant ?? LabelVariant.CONTAINED;

    return [
      styles['root'],
      styles[variant],
      styles[`${variant}${capitalize(color)}`],
      styles[`size${capitalize(size)}`],
      styles[`${variant}Size${capitalize(size)}`],
    ];
  },
})<LabelProps>(({
  theme: _theme,
  color: colorProps,
  variant: variantProps,
  size: sizeProps,
  borderType: borderTypeProps,
}) => {
  const theme = _theme as typeof BaseTheme;
  const defaultProps = theme.components?.['Label']?.defaultProps;

  const color: LabelColor = colorProps ?? defaultProps?.color ?? LabelColor.PRIMARY;
  const size = sizeProps ?? defaultProps?.size ?? LabelSize.MEDIUM;
  const variant = variantProps ?? defaultProps?.variant ?? LabelVariant.CONTAINED;
  const frameStyle = borderTypeProps ?? defaultProps?.frameStyle ?? LabelBorderType.RECTANGLE;

  let colorStyles = {};

  if (color === LabelColor.GREY) {
    colorStyles = {
      ...(variant === 'outlined' && {
        backgroundColor: theme.palette[color][50],
        color: theme.palette[color][700],
        borderColor: theme.palette[color][500],
      }),
      ...(variant === 'contained' && {
        backgroundColor: theme.palette[color][500],
        color: theme.palette.common.white,
        borderColor: 'transparent',
      }),
      ...(variant === 'text' && {
        color: theme.palette[color][500],
        borderColor: 'transparent',
      }),
    };
  } else {
    colorStyles = {
      ...(variant === 'outlined' && {
        backgroundColor: theme.palette[color].lighter,
        color: theme.palette[color].dark,
        borderColor: theme.palette[color].main,
      }),
      ...(variant === 'contained' && {
        backgroundColor: theme.palette[color].main,
        color: theme.palette[color].contrastText ?? theme.palette['common'].white,
        borderColor: 'transparent',
      }),
      ...(variant === 'text' && {
        color: theme.palette[color].main,
        borderColor: 'transparent',
      }),
    };
  }

  return {
    display: 'flex',
    gap: theme.spacing(1),
    width: 'fit-content',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'fit-content',
    flexShrink: 0,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: '6px',
    ...colorStyles,
    ...(size === 'small' && {
      padding: theme.spacing(0.25, 1.75),
      ...theme.typography.caption,
    }),
    ...(size === 'medium' && {
      padding: theme.spacing(0.25, 2.25),
      ...theme.typography.body2,
    }),
    ...(size === 'large' && {
      padding: theme.spacing(0.75, 2.75),
      ...theme.typography.body2,
    }),
    fontWeight: 600,
    ...(frameStyle === 'round' && {
      borderRadius: '28px',
    }),
    ...(frameStyle === 'rectangle' && {
      borderRadius: size === 'large' ? '6px' : '4px',
    }),
  };
});

export { LabelRoot, LabelVariant, LabelSize, LabelColor, LabelBorderType };
export type { LabelProps, LabelClasses, LabelClassKey };
export default LabelRoot;
